// src/pages/AnalyticsPage.js
import { useState, useEffect } from 'react';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import Sidebar from '../components/Sidebar';

const EyeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);
const BoxIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
    <polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/>
  </svg>
);
const LockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
  </svg>
);

export default function AnalyticsPage() {
  const { user } = useAuth();
  const [stores, setStores] = useState([]);
  const [selectedStoreId, setSelectedStoreId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.getStores().then(all => {
      const owned = all.filter(s => s.ownerId === user?.storeOwner?.id);
      setStores(owned);
      if (owned.length) setSelectedStoreId(owned[0].id);
    });
  }, [user]);

  useEffect(() => {
    if (!selectedStoreId) return;
    setLoading(true);
    api.getAnalytics(selectedStoreId)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [selectedStoreId]);

  const totalProductViews = data?.productViews.reduce((s, r) => s + r.views, 0) || 0;
  const totalUniqueProductVisitors = data?.productViews.reduce((s, r) => s + r.uniqueVisitors, 0) || 0;
  const totalLocks = data?.dealLocks.reduce((s, r) => s + r.locks, 0) || 0;

  return (
    <div className="dash-root">
      <Sidebar active="analytics" />
      <main className="dash-main">
        <div className="page-content">
          <div className="page-header">
            <div>
              <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem', fontWeight: 700, letterSpacing: '-0.5px' }}>
                Analytics
              </h1>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginTop: 2 }}>
                Shopper engagement for your store
              </p>
            </div>
            {stores.length > 1 && (
              <select
                value={selectedStoreId}
                onChange={e => setSelectedStoreId(e.target.value)}
                style={{
                  padding: '8px 32px 8px 12px',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-sm)',
                  background: 'var(--surface)',
                  color: 'var(--text)',
                  fontFamily: 'inherit',
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  outline: 'none',
                  appearance: 'none',
                  backgroundImage: "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%2394a3b8' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M2 5l6 6 6-6'/%3e%3c/svg%3e\")",
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 10px center',
                  backgroundSize: '12px',
                }}
              >
                {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            )}
          </div>

          {loading && (
            <div style={{ padding: 48, display: 'flex', justifyContent: 'center' }}>
              <div className="spinner" />
            </div>
          )}

          {!loading && data && (
            <>
              {/* Summary cards */}
              <div className="stat-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                <div className="stat-card">
                  <div className="stat-icon indigo"><EyeIcon /></div>
                  <div className="stat-value">{data.storeViews}</div>
                  <div className="stat-label">Store Views</div>
                  <div className="stat-sub">{data.storeUniqueVisitors} unique visitors</div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon green"><BoxIcon /></div>
                  <div className="stat-value">{totalProductViews}</div>
                  <div className="stat-label">Product Views</div>
                  <div className="stat-sub">{totalUniqueProductVisitors} unique visitors</div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon amber"><LockIcon /></div>
                  <div className="stat-value">{totalLocks}</div>
                  <div className="stat-label">Deal Locks</div>
                  <div className="stat-sub">Total redemptions</div>
                </div>
              </div>

              {/* Product views table */}
              <div style={{ marginBottom: 24 }}>
                <div className="section-header">
                  <h2>Views by Product</h2>
                </div>
                <div className="table-card">
                  {data.productViews.length === 0 ? (
                    <div className="empty-state" style={{ padding: '32px 24px' }}>
                      <BoxIcon />
                      <h3>No product views yet</h3>
                      <p>Views will appear here as shoppers browse your products.</p>
                    </div>
                  ) : (
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Product</th>
                          <th style={{ textAlign: 'right' }}>Total Views</th>
                          <th style={{ textAlign: 'right' }}>Unique Visitors</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.productViews.map(r => (
                          <tr key={r.product.id}>
                            <td>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                <div className="product-thumb">
                                  {r.product.imageUrl
                                    ? <img src={r.product.imageUrl} alt={r.product.name} />
                                    : <BoxIcon />
                                  }
                                </div>
                                <span style={{ fontWeight: 500 }}>{r.product.name}</span>
                              </div>
                            </td>
                            <td style={{ textAlign: 'right', fontWeight: 600 }}>{r.views}</td>
                            <td style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>{r.uniqueVisitors}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>

              {/* Deal locks table */}
              <div>
                <div className="section-header">
                  <h2>Locks by Deal</h2>
                </div>
                <div className="table-card">
                  {data.dealLocks.length === 0 ? (
                    <div className="empty-state" style={{ padding: '32px 24px' }}>
                      <LockIcon />
                      <h3>No deal locks yet</h3>
                      <p>Lock counts will appear here as shoppers redeem your coupons.</p>
                    </div>
                  ) : (
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Deal</th>
                          <th style={{ textAlign: 'right' }}>Locks</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.dealLocks.map(r => {
                          const discount = r.coupon.discountType === 'PERCENTAGE'
                            ? `${r.coupon.discountValue}% OFF`
                            : `₹${r.coupon.discountValue} OFF`;
                          return (
                            <tr key={r.coupon.id}>
                              <td>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                  <span style={{ fontWeight: 500 }}>{r.coupon.title}</span>
                                  <span className="badge badge-indigo">{discount}</span>
                                </div>
                              </td>
                              <td style={{ textAlign: 'right', fontWeight: 600 }}>{r.locks}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </>
          )}

          {!loading && !data && selectedStoreId && (
            <div className="table-card">
              <div className="empty-state">
                <p style={{ color: 'var(--text-muted)' }}>Could not load analytics. Try again later.</p>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
